/**
 * Хуки TanStack Query на каждый эндпоинт `docs/spec/_api-contract.md`.
 *
 * Ключи запросов лежат в `queryKeys` — инвалидировать нужно ровно их, руками массивы
 * не собирать. Мутации по делу сами кладут свежий `CaseDetailDto` в кэш карточки
 * и точечно инвалидируют списки и дашборд.
 */

import { useEffect, useState } from 'react';
import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
    type QueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, api, downloadFile } from './client';
import { periodKey, periodToParams, type PeriodValue } from '../lib/deadlines';
import type {
    AssignRequest,
    CaseDetailDto,
    CaseListParams,
    CaseListResponse,
    ClinicPatchRequest,
    ClinicResponse,
    CommentRequest,
    CommentResponse,
    ConfirmSegmentsRequest,
    CreateUserRequest,
    DashboardDto,
    DashboardListDto,
    DashboardListParams,
    DepartmentDetailDto,
    DepartmentInput,
    DepartmentResponse,
    DepartmentsResponse,
    DraftSaveRequest,
    EscalateRequest,
    ExtendDecideRequest,
    ExtendRequest,
    FollowupQueued,
    FollowupRequest,
    FollowupResponse,
    FollowupStatus,
    HealthDto,
    InboxAcceptedResponse,
    InboxItemDto,
    InboxListParams,
    InboxListResponse,
    LoginRequest,
    MaxLinkResponse,
    MeDto,
    NotificationsParams,
    NotificationsReadRequest,
    NotificationsReadResponse,
    NotificationsResponse,
    OkResponse,
    PatchCaseRequest,
    PatchMeRequest,
    ReopenRequest,
    ReviewRequest,
    RoutingPatchRequest,
    RoutingResponse,
    SendRequest,
    SplitOutResponse,
    UpdateUserRequest,
    UserResponse,
    UsersResponse,
} from './types';

/* ─────────────────────────── Ключи ─────────────────────────── */

/**
 * Единый реестр ключей. Префиксы вложены, поэтому инвалидация верхнего уровня
 * (`queryKeys.cases.all()`) накрывает и списки, и карточки.
 */
export const queryKeys = {
    me: () => ['me'] as const,
    health: () => ['health'] as const,
    dashboard: {
        all: () => ['dashboard'] as const,
        main: (period: PeriodValue) => ['dashboard', 'main', periodKey(period)] as const,
        list: (params: DashboardListParams, period?: PeriodValue) =>
            ['dashboard', 'list', params, period ? periodKey(period) : null] as const,
        department: (departmentId: string, period: PeriodValue) =>
            ['dashboard', 'department', departmentId, periodKey(period)] as const,
    },
    cases: {
        all: () => ['cases'] as const,
        lists: () => ['cases', 'list'] as const,
        list: (params: CaseListParams) => ['cases', 'list', params] as const,
        details: () => ['cases', 'detail'] as const,
        detail: (caseId: string) => ['cases', 'detail', caseId] as const,
    },
    inbox: {
        all: () => ['inbox'] as const,
        lists: () => ['inbox', 'list'] as const,
        list: (params: InboxListParams) => ['inbox', 'list', params] as const,
        items: () => ['inbox', 'item'] as const,
        item: (inboxId: string) => ['inbox', 'item', inboxId] as const,
    },
    notifications: {
        all: () => ['notifications'] as const,
        list: (params: NotificationsParams) => ['notifications', 'list', params] as const,
        infinite: (limit: number) => ['notifications', 'infinite', limit] as const,
    },
    settings: {
        all: () => ['settings'] as const,
        users: () => ['settings', 'users'] as const,
        departments: () => ['settings', 'departments'] as const,
        routing: () => ['settings', 'routing'] as const,
        clinic: () => ['settings', 'clinic'] as const,
    },
};

/** Необязательные подкрутки запроса — то, что реально нужно экранам. */
export interface QueryTweaks {
    enabled?: boolean;
    staleTime?: number;
    refetchInterval?: number | false;
    refetchOnWindowFocus?: boolean;
    retry?: number | false;
}

/* ─────────────────────────── Профиль ─────────────────────────── */

export function useMe(tweaks?: QueryTweaks): UseQueryResult<MeDto, ApiError> {
    return useQuery<MeDto, ApiError>({
        queryKey: queryKeys.me(),
        queryFn: () => api.get<MeDto>('/me'),
        staleTime: 60_000,
        retry: false,
        ...tweaks,
    });
}

export function useLogin(): UseMutationResult<MeDto, ApiError, LoginRequest> {
    const qc = useQueryClient();
    return useMutation<MeDto, ApiError, LoginRequest>({
        mutationFn: (body) => api.post<MeDto>('/auth/login', body),
        onSuccess: (me) => {
            qc.setQueryData(queryKeys.me(), me);
        },
    });
}

export function useLogout(): UseMutationResult<OkResponse, ApiError, void> {
    const qc = useQueryClient();
    return useMutation<OkResponse, ApiError, void>({
        mutationFn: () => api.post<OkResponse>('/auth/logout'),
        onSuccess: () => {
            qc.clear();
        },
    });
}

export function useUpdateMe(): UseMutationResult<MeDto, ApiError, PatchMeRequest> {
    const qc = useQueryClient();
    return useMutation<MeDto, ApiError, PatchMeRequest>({
        mutationFn: (body) => api.patch<MeDto>('/me', body),
        onSuccess: (me) => {
            qc.setQueryData(queryKeys.me(), me);
        },
    });
}

export function useMaxLink(): UseMutationResult<MaxLinkResponse, ApiError, void> {
    const qc = useQueryClient();
    return useMutation<MaxLinkResponse, ApiError, void>({
        mutationFn: () => api.post<MaxLinkResponse>('/me/max-link'),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: queryKeys.me() });
        },
    });
}

/* ─────────────────────────── Дашборд ─────────────────────────── */

/**
 * Дашборд повторяет запрос при сбое сети или сервера без ограничения числа попыток (пауза растёт до 30 с): экран
 * показывает ошибку после первой неудачи (`pendingFailure`), а повторы идут в фоне и при успехе её убирают.
 * Ошибки прав и «не найдено» (4xx) не повторяются.
 */
const DASHBOARD_RETRY_MAX_DELAY_MS = 30_000;

export function useDashboard(
    period: PeriodValue,
    tweaks?: QueryTweaks,
): UseQueryResult<DashboardDto, ApiError> {
    return useQuery<DashboardDto, ApiError>({
        queryKey: queryKeys.dashboard.main(period),
        queryFn: () => api.get<DashboardDto>('/dashboard', { ...periodToParams(period) }),
        staleTime: 30_000,
        retry: (_failureCount, error) => !(error instanceof ApiError && error.status >= 400 && error.status < 500),
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, DASHBOARD_RETRY_MAX_DELAY_MS),
        ...tweaks,
    });
}

/** Список дел по клику на плитку, ячейку календаря, строку нагрузки или категорию. */
export function useDashboardList(
    params: DashboardListParams | null,
    period?: PeriodValue,
    tweaks?: QueryTweaks,
): UseQueryResult<DashboardListDto, ApiError> {
    const enabled = tweaks?.enabled !== false && params !== null;
    return useQuery<DashboardListDto, ApiError>({
        queryKey: queryKeys.dashboard.list(params ?? ({ kpi: 'open' } as DashboardListParams), period),
        queryFn: () =>
            api.get<DashboardListDto>('/dashboard/list', {
                ...(period ? periodToParams(period) : {}),
                ...(params as unknown as Record<string, string | number | undefined>),
            }),
        staleTime: 30_000,
        ...tweaks,
        enabled,
    });
}

/** Раскрытая строка «Роста по отделениям». `departmentId` = uuid либо `none`. */
export function useDashboardDepartment(
    departmentId: string | null,
    period: PeriodValue,
    tweaks?: QueryTweaks,
): UseQueryResult<DepartmentDetailDto, ApiError> {
    const id = departmentId ?? 'none';
    return useQuery<DepartmentDetailDto, ApiError>({
        queryKey: queryKeys.dashboard.department(id, period),
        queryFn: () =>
            api.get<DepartmentDetailDto>('/dashboard/department/' + id, { ...periodToParams(period) }),
        staleTime: 30_000,
        ...tweaks,
    });
}

/* ─────────────────────────── Дела ─────────────────────────── */

export function useCases(
    params: CaseListParams,
    tweaks?: QueryTweaks,
): UseQueryResult<CaseListResponse, ApiError> {
    return useQuery<CaseListResponse, ApiError>({
        queryKey: queryKeys.cases.list(params),
        queryFn: () => api.get<CaseListResponse>('/cases', { ...params }),
        staleTime: 15_000,
        ...tweaks,
    });
}

export function useCase(
    caseId: string | null | undefined,
    tweaks?: QueryTweaks,
): UseQueryResult<CaseDetailDto, ApiError> {
    return useQuery<CaseDetailDto, ApiError>({
        queryKey: queryKeys.cases.detail(caseId ?? ''),
        queryFn: () => api.get<CaseDetailDto>('/cases/' + caseId),
        staleTime: 10_000,
        ...tweaks,
        enabled: Boolean(caseId) && tweaks?.enabled !== false,
    });
}

/** Достаёт `CaseDetailDto` из ответа действия: он приходит либо сам, либо полем `case`. */
function pickDetail(data: unknown): CaseDetailDto | null {
    if (!data || typeof data !== 'object') return null;
    const obj = data as Record<string, unknown>;
    if ('permissions' in obj && 'case' in obj) return obj as unknown as CaseDetailDto;
    const nested = obj.case;
    if (nested && typeof nested === 'object' && 'permissions' in (nested as Record<string, unknown>)) {
        return nested as CaseDetailDto;
    }
    return null;
}

/** Точечная инвалидация после любого действия по делу. */
export function invalidateCase(qc: QueryClient, caseId: string, data?: unknown): void {
    const detail = pickDetail(data);
    if (detail) qc.setQueryData(queryKeys.cases.detail(caseId), detail);
    else void qc.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) });
    void qc.invalidateQueries({ queryKey: queryKeys.cases.lists() });
    void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all() });
}

/** То же плюс колокольчик: действие породило уведомление. */
function invalidateCaseWithNotifications(qc: QueryClient, caseId: string, data?: unknown): void {
    invalidateCase(qc, caseId, data);
    void qc.invalidateQueries({ queryKey: queryKeys.notifications.all() });
    void qc.invalidateQueries({ queryKey: queryKeys.me() });
}

function useCaseAction<TVars, TData = CaseDetailDto>(
    caseId: string,
    mutationFn: (vars: TVars) => Promise<TData>,
    withNotifications = false,
): UseMutationResult<TData, ApiError, TVars> {
    const qc = useQueryClient();
    return useMutation<TData, ApiError, TVars>({
        mutationFn,
        onSuccess: (data) => {
            if (withNotifications) invalidateCaseWithNotifications(qc, caseId, data);
            else invalidateCase(qc, caseId, data);
        },
    });
}

const TURN_POLL_MS = 2000;
/** Сколько карточка ждёт ответа куратора; дальше ход не теряется — ответ появится в переписке сам. */
const TURN_WAIT_MS = 6 * 60_000;

export const TURN_STILL_PENDING = 'Ответ куратора ещё готовится — он появится в переписке, когда будет готов.';

/** Ключ отправки: повтор того же запроса (обрыв связи) не ставит второй ход. */
function idempotencyKey(): Record<string, string> {
    const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : null;
    return uuid ? { 'Idempotency-Key': uuid } : {};
}

/** Сбой связи или сервера (деплой, обрыв сети): запрос можно повторить. */
function transient(error: unknown): boolean {
    return error instanceof ApiError && (error.status === 0 || error.status >= 500);
}

/** Опрос хода кодового движка (ответ 202) до исхода; кратковременные сбои связи опрос не обрывают. */
export async function waitForTurn(caseId: string, jobId: string): Promise<FollowupResponse> {
    const until = Date.now() + TURN_WAIT_MS;
    for (;;) {
        await new Promise((resolve) => window.setTimeout(resolve, TURN_POLL_MS));
        let status: FollowupStatus;
        try {
            status = await api.get<FollowupStatus>('/cases/' + caseId + '/followup/' + jobId);
        } catch (error) {
            if (transient(error) && Date.now() < until) continue;
            throw error;
        }
        if (status.status === 'done') return { case: status.case, turn: status.turn };
        if (status.status === 'failed') throw new ApiError(status.message, 'TURN_FAILED', 500);
        if (Date.now() >= until) throw new ApiError(TURN_STILL_PENDING, 'TURN_PENDING', 202);
    }
}

export function useFollowup(caseId: string) {
    return useCaseAction<FollowupRequest, FollowupResponse>(caseId, async (body) => {
        const headers = idempotencyKey();
        const send = () => api.post<FollowupResponse | FollowupQueued>('/cases/' + caseId + '/followup', body, { headers });
        let res: FollowupResponse | FollowupQueued;
        try {
            res = await send();
        } catch (error) {
            // Ответ потерялся (обрыв связи): повтор с тем же ключом вернёт уже поставленный ход, а не второй.
            if (!transient(error) || !headers['Idempotency-Key']) throw error;
            res = await send();
        }
        // n8n отвечает ходом сразу; кодовый движок ставит ход в очередь (202) — ждём исход опросом.
        if ('job_id' in res && res.status === 'pending') return waitForTurn(caseId, res.job_id);
        return res as FollowupResponse;
    });
}

/**
 * Ход, начатый до перезагрузки или в другой вкладке (`pending_turn` дела): опрос до исхода, затем дело
 * перечитывается. Пока опрос идёт, карточка показывает «ответ готовится».
 */
export function usePendingTurn(caseId: string, jobId: string | null): boolean {
    const qc = useQueryClient();
    const [waiting, setWaiting] = useState(false);
    // Номер попытки: после сбоя или истечения ожидания опрос того же хода возобновляется.
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        if (!jobId) return;
        let cancelled = false;
        let retry: number | undefined;
        setWaiting(true);
        waitForTurn(caseId, jobId)
            .then((data) => {
                if (cancelled) return;
                invalidateCaseWithNotifications(qc, caseId, data);
                setWaiting(false);
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                void qc.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) });
                // Отказ хода — исход; сбой связи или долгое ожидание — новая попытка через полминуты.
                if (error instanceof ApiError && error.code === 'TURN_FAILED') setWaiting(false);
                else retry = window.setTimeout(() => setAttempt((n) => n + 1), 30_000);
            });
        return () => {
            cancelled = true;
            if (retry !== undefined) window.clearTimeout(retry);
        };
    }, [caseId, jobId, qc, attempt]);
    return waiting && jobId !== null;
}

export function useToggleCheck(caseId: string) {
    return useCaseAction<{ check_id: string; done: boolean }>(caseId, (body) =>
        api.post<CaseDetailDto>('/cases/' + caseId + '/check', body),
    );
}

export function useReviewCase(caseId: string) {
    return useCaseAction<ReviewRequest>(caseId, (body) =>
        api.post<CaseDetailDto>('/cases/' + caseId + '/review', body),
    );
}

export function useUndoMessage(caseId: string) {
    return useCaseAction<number>(caseId, (messageId) =>
        api.post<CaseDetailDto>('/cases/' + caseId + '/messages/' + messageId + '/undo'),
    );
}

export function useSplitOutMessage(caseId: string) {
    const qc = useQueryClient();
    return useMutation<SplitOutResponse, ApiError, number>({
        mutationFn: (messageId) =>
            api.post<SplitOutResponse>('/cases/' + caseId + '/messages/' + messageId + '/split-out'),
        onSuccess: (data) => {
            invalidateCase(qc, caseId, data);
            void qc.invalidateQueries({ queryKey: queryKeys.inbox.all() });
        },
    });
}

export function useGenerateDraft(caseId: string) {
    return useCaseAction<void>(caseId, () =>
        api.post<CaseDetailDto>('/cases/' + caseId + '/draft/generate'),
    );
}

export function useSaveDraft(caseId: string) {
    return useCaseAction<DraftSaveRequest>(caseId, (body) =>
        api.put<CaseDetailDto>('/cases/' + caseId + '/draft', body),
    );
}

export function useToApproval(caseId: string) {
    return useCaseAction<void>(
        caseId,
        () => api.post<CaseDetailDto>('/cases/' + caseId + '/to-approval'),
        true,
    );
}

export function useApproveCase(caseId: string) {
    return useCaseAction<void>(
        caseId,
        () => api.post<CaseDetailDto>('/cases/' + caseId + '/approve'),
        true,
    );
}

export function useSendCase(caseId: string) {
    return useCaseAction<SendRequest>(
        caseId,
        (body) => api.post<CaseDetailDto>('/cases/' + caseId + '/send', body),
        true,
    );
}

export function useReopenCase(caseId: string) {
    return useCaseAction<ReopenRequest>(
        caseId,
        (body) => api.post<CaseDetailDto>('/cases/' + caseId + '/reopen', body),
        true,
    );
}

export function useExtendCase(caseId: string) {
    return useCaseAction<ExtendRequest>(
        caseId,
        (body) => api.post<CaseDetailDto>('/cases/' + caseId + '/extend', body),
        true,
    );
}

export function useDecideExtension(caseId: string) {
    return useCaseAction<ExtendDecideRequest>(
        caseId,
        (body) => api.post<CaseDetailDto>('/cases/' + caseId + '/extend/decide', body),
        true,
    );
}

export function useEscalateCase(caseId: string) {
    return useCaseAction<EscalateRequest>(
        caseId,
        (body) => api.post<CaseDetailDto>('/cases/' + caseId + '/escalate', body),
        true,
    );
}

export function useAssignCase(caseId: string) {
    return useCaseAction<AssignRequest>(
        caseId,
        (body) => api.post<CaseDetailDto>('/cases/' + caseId + '/assign', body),
        true,
    );
}

export function useTakeCase(caseId: string) {
    return useCaseAction<void>(caseId, () => api.post<CaseDetailDto>('/cases/' + caseId + '/take'), true);
}

export function usePatchCase(caseId: string) {
    return useCaseAction<PatchCaseRequest>(caseId, (body) =>
        api.patch<CaseDetailDto>('/cases/' + caseId, body),
    );
}

export function useAddComment(caseId: string) {
    const qc = useQueryClient();
    return useMutation<CommentResponse, ApiError, CommentRequest>({
        mutationFn: (body) => api.post<CommentResponse>('/cases/' + caseId + '/comments', body),
        onSuccess: () => {
            invalidateCase(qc, caseId);
        },
    });
}

/* ─────────────────────────── Входящие ─────────────────────────── */

export function useInbox(
    params: InboxListParams,
    tweaks?: QueryTweaks,
): UseQueryResult<InboxListResponse, ApiError> {
    return useQuery<InboxListResponse, ApiError>({
        queryKey: queryKeys.inbox.list(params),
        queryFn: () => api.get<InboxListResponse>('/inbox', { ...params }),
        staleTime: 10_000,
        ...tweaks,
    });
}

/**
 * Статус одного входящего. Пока `new` или `processing` — опрос раз в 2 секунды,
 * после `done` / `failed` / `needs_confirm` опрос выключается сам.
 */
export function useInboxItem(
    inboxId: string | null | undefined,
    tweaks?: QueryTweaks,
): UseQueryResult<InboxItemDto, ApiError> {
    const [pollMs, setPollMs] = useState<number | false>(2000);
    const query = useQuery<InboxItemDto, ApiError>({
        queryKey: queryKeys.inbox.item(inboxId ?? ''),
        queryFn: () => api.get<InboxItemDto>('/inbox/' + inboxId),
        staleTime: 0,
        refetchInterval: pollMs,
        ...tweaks,
        enabled: Boolean(inboxId) && tweaks?.enabled !== false,
    });
    const status = query.data?.status;
    useEffect(() => {
        const pending = status === 'new' || status === 'processing' || status === undefined;
        setPollMs(pending ? 2000 : false);
    }, [status]);
    return query;
}

/** `POST /api/inbox/manual` — multipart. Собирать `FormData` на стороне формы. */
export function useCreateManualInbox(): UseMutationResult<InboxAcceptedResponse, ApiError, FormData> {
    const qc = useQueryClient();
    return useMutation<InboxAcceptedResponse, ApiError, FormData>({
        mutationFn: (form) => api.upload<InboxAcceptedResponse>('/inbox/manual', form),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: queryKeys.inbox.all() });
        },
    });
}

export function useRetryInbox(): UseMutationResult<InboxAcceptedResponse, ApiError, string> {
    const qc = useQueryClient();
    return useMutation<InboxAcceptedResponse, ApiError, string>({
        mutationFn: (inboxId) => api.post<InboxAcceptedResponse>('/inbox/' + inboxId + '/retry'),
        onSuccess: (_data, inboxId) => {
            void qc.invalidateQueries({ queryKey: queryKeys.inbox.item(inboxId) });
            void qc.invalidateQueries({ queryKey: queryKeys.inbox.lists() });
        },
    });
}

export interface ConfirmSegmentsVars extends ConfirmSegmentsRequest {
    inboxId: string;
}

export function useConfirmSegments(): UseMutationResult<
    InboxAcceptedResponse,
    ApiError,
    ConfirmSegmentsVars
> {
    const qc = useQueryClient();
    return useMutation<InboxAcceptedResponse, ApiError, ConfirmSegmentsVars>({
        mutationFn: ({ inboxId, groups }) =>
            api.post<InboxAcceptedResponse>('/inbox/' + inboxId + '/confirm-segments', { groups }),
        onSuccess: (_data, vars) => {
            void qc.invalidateQueries({ queryKey: queryKeys.inbox.item(vars.inboxId) });
            void qc.invalidateQueries({ queryKey: queryKeys.inbox.lists() });
        },
    });
}

/* ─────────────────────────── Уведомления ─────────────────────────── */

export function useNotifications(
    params: NotificationsParams = {},
    tweaks?: QueryTweaks,
): UseQueryResult<NotificationsResponse, ApiError> {
    return useQuery<NotificationsResponse, ApiError>({
        queryKey: queryKeys.notifications.list(params),
        queryFn: () =>
            api.get<NotificationsResponse>('/notifications', {
                limit: params.limit,
                before: params.before ?? undefined,
            }),
        staleTime: 30_000,
        ...tweaks,
    });
}

/** Курсорная выдача колокольчика: `before` → `next_before`, пока `has_more`. */
export function useNotificationsInfinite(limit = 20, tweaks?: QueryTweaks) {
    return useInfiniteQuery<NotificationsResponse, ApiError>({
        queryKey: queryKeys.notifications.infinite(limit),
        queryFn: ({ pageParam }) =>
            api.get<NotificationsResponse>('/notifications', {
                limit,
                before: (pageParam as number | null) ?? undefined,
            }),
        initialPageParam: null as number | null,
        getNextPageParam: (lastPage: NotificationsResponse) =>
            lastPage.has_more ? lastPage.next_before : null,
        staleTime: 30_000,
        ...tweaks,
    });
}

export function useMarkNotificationsRead(): UseMutationResult<
    NotificationsReadResponse,
    ApiError,
    NotificationsReadRequest
> {
    const qc = useQueryClient();
    return useMutation<NotificationsReadResponse, ApiError, NotificationsReadRequest>({
        mutationFn: (body) => api.post<NotificationsReadResponse>('/notifications/read', body),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: queryKeys.notifications.all() });
            void qc.invalidateQueries({ queryKey: queryKeys.me() });
        },
    });
}

/* ─────────────────────────── Настройки ─────────────────────────── */

export function useSettingsUsers(tweaks?: QueryTweaks): UseQueryResult<UsersResponse, ApiError> {
    return useQuery<UsersResponse, ApiError>({
        queryKey: queryKeys.settings.users(),
        queryFn: () => api.get<UsersResponse>('/settings/users'),
        staleTime: 60_000,
        ...tweaks,
    });
}

export function useCreateUser(): UseMutationResult<UserResponse, ApiError, CreateUserRequest> {
    const qc = useQueryClient();
    return useMutation<UserResponse, ApiError, CreateUserRequest>({
        mutationFn: (body) => api.post<UserResponse>('/settings/users', body),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: queryKeys.settings.users() });
        },
    });
}

export interface UpdateUserVars extends UpdateUserRequest {
    id: string;
}

export function useUpdateUser(): UseMutationResult<UserResponse, ApiError, UpdateUserVars> {
    const qc = useQueryClient();
    return useMutation<UserResponse, ApiError, UpdateUserVars>({
        mutationFn: ({ id, ...body }) => api.patch<UserResponse>('/settings/users/' + id, body),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: queryKeys.settings.users() });
            void qc.invalidateQueries({ queryKey: queryKeys.me() });
        },
    });
}

export function useSettingsDepartments(
    tweaks?: QueryTweaks,
): UseQueryResult<DepartmentsResponse, ApiError> {
    return useQuery<DepartmentsResponse, ApiError>({
        queryKey: queryKeys.settings.departments(),
        queryFn: () => api.get<DepartmentsResponse>('/settings/departments'),
        staleTime: 60_000,
        ...tweaks,
    });
}

export function useCreateDepartment(): UseMutationResult<
    DepartmentResponse,
    ApiError,
    DepartmentInput
> {
    const qc = useQueryClient();
    return useMutation<DepartmentResponse, ApiError, DepartmentInput>({
        mutationFn: (body) => api.post<DepartmentResponse>('/settings/departments', body),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: queryKeys.settings.departments() });
        },
    });
}

export interface UpdateDepartmentVars extends DepartmentInput {
    id: string;
}

export function useUpdateDepartment(): UseMutationResult<
    DepartmentResponse,
    ApiError,
    UpdateDepartmentVars
> {
    const qc = useQueryClient();
    return useMutation<DepartmentResponse, ApiError, UpdateDepartmentVars>({
        mutationFn: ({ id, ...body }) =>
            api.patch<DepartmentResponse>('/settings/departments/' + id, body),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: queryKeys.settings.departments() });
        },
    });
}

export function useSettingsRouting(tweaks?: QueryTweaks): UseQueryResult<RoutingResponse, ApiError> {
    return useQuery<RoutingResponse, ApiError>({
        queryKey: queryKeys.settings.routing(),
        queryFn: () => api.get<RoutingResponse>('/settings/routing'),
        staleTime: 60_000,
        ...tweaks,
    });
}

export function useSaveRouting(): UseMutationResult<RoutingResponse, ApiError, RoutingPatchRequest> {
    const qc = useQueryClient();
    return useMutation<RoutingResponse, ApiError, RoutingPatchRequest>({
        mutationFn: (body) => api.patch<RoutingResponse>('/settings/routing', body),
        onSuccess: (data) => {
            qc.setQueryData(queryKeys.settings.routing(), data);
        },
    });
}

export function useSettingsClinic(tweaks?: QueryTweaks): UseQueryResult<ClinicResponse, ApiError> {
    return useQuery<ClinicResponse, ApiError>({
        queryKey: queryKeys.settings.clinic(),
        queryFn: () => api.get<ClinicResponse>('/settings/clinic'),
        staleTime: 60_000,
        ...tweaks,
    });
}

export function useUpdateClinic(): UseMutationResult<ClinicResponse, ApiError, ClinicPatchRequest> {
    const qc = useQueryClient();
    return useMutation<ClinicResponse, ApiError, ClinicPatchRequest>({
        mutationFn: (body) => api.patch<ClinicResponse>('/settings/clinic', body),
        onSuccess: (data) => {
            qc.setQueryData(queryKeys.settings.clinic(), data);
            void qc.invalidateQueries({ queryKey: queryKeys.me() });
        },
    });
}

/* ─────────────────────────── Отчёт и служебное ─────────────────────────── */

/** Скачивание `summary.xlsx`: имя файла приходит в `Content-Disposition`. */
export function useDownloadReport(): UseMutationResult<string, ApiError, PeriodValue> {
    return useMutation<string, ApiError, PeriodValue>({
        mutationFn: (period) =>
            downloadFile('/reports/summary.xlsx', { ...periodToParams(period) }, 'gms-report.xlsx'),
    });
}

/** `/health` живёт вне `/api` и без токена — потому идёт мимо `api.get`. */
export function useHealth(tweaks?: QueryTweaks): UseQueryResult<HealthDto, ApiError> {
    return useQuery<HealthDto, ApiError>({
        queryKey: queryKeys.health(),
        queryFn: async () => {
            const response = await fetch('/health', { credentials: 'include' });
            return (await response.json()) as HealthDto;
        },
        staleTime: 60_000,
        retry: false,
        ...tweaks,
    });
}
